import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import { useOrg } from '../lib/org';
import { Modal, Field, TextInput, Select, Textarea, FormActions, dbErrorMessage } from './form';
import { PhotoStrip } from './InventoryForms';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CLIENT_TYPES = ['person', 'company'] as const;
const PROPERTY_TYPES = ['villa', 'apartment', 'house', 'restaurant', 'bar', 'hotel', 'building', 'other'] as const;
const CRITICALITY = ['low', 'medium', 'high', 'critical'] as const;
const AVAILABILITY = ['available', 'unavailable', 'retired'] as const;
const WO_TYPES = ['preventive', 'corrective', 'inspection', 'emergency', 'out_of_contract'] as const;
const PRIORITIES = ['low', 'medium', 'high', 'urgent'] as const;

type BaseProps<T> = { open: boolean; onClose: () => void; onSaved: () => void; initial?: T | null };

// ── Client ─────────────────────────────────────────────────────────────────
export type ClientRow = { id: string; name: string; client_type: string; email: string | null; phone: string | null; notes?: string | null };
export function ClientForm({ open, onClose, onSaved, initial }: BaseProps<ClientRow>) {
  const { t } = useTranslation();
  const { id: orgId } = useOrg();
  const [f, setF] = useState({ name: '', client_type: 'person', email: '', phone: '', notes: '' });
  const [err, setErr] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setErr({});
    setF({
      name: initial?.name ?? '', client_type: initial?.client_type ?? 'person',
      email: initial?.email ?? '', phone: initial?.phone ?? '', notes: initial?.notes ?? '',
    });
  }, [open, initial]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const er: Record<string, string> = {};
    if (!f.name.trim()) er.name = t('form.errRequired');
    if (f.email.trim() && !EMAIL_RE.test(f.email.trim())) er.email = t('form.errEmail');
    setErr(er);
    if (Object.keys(er).length) return;
    setBusy(true);
    const payload = {
      name: f.name.trim(), client_type: f.client_type,
      email: f.email.trim() || null, phone: f.phone.trim() || null, notes: f.notes.trim() || null,
    };
    const res = initial?.id
      ? await supabase.from('clients').update(payload).eq('id', initial.id)
      : await supabase.from('clients').insert({ ...payload, organization_id: orgId } as any);
    setBusy(false);
    if (res.error) { setErr({ _: dbErrorMessage(t, res.error) }); return; }
    onSaved(); onClose();
  }

  return (
    <Modal open={open} onClose={onClose} title={initial?.id ? t('form.editClient') : t('form.newClient')}>
      <form onSubmit={submit} className="flex flex-col gap-4 p-5">
        {err._ && <div className="rounded-md bg-st-overdue-bg px-3 py-2 text-sm text-st-overdue-fg">{err._}</div>}
        <Field label={t('form.name')} required error={err.name}>
          <TextInput value={f.name} error={!!err.name} onChange={(e) => setF({ ...f, name: e.target.value })} autoFocus />
        </Field>
        <Field label={t('form.type')}>
          <Select value={f.client_type} onChange={(e) => setF({ ...f, client_type: e.target.value })}>
            {CLIENT_TYPES.map((c) => <option key={c} value={c}>{t('clientType.' + c)}</option>)}
          </Select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('form.email')} error={err.email}>
            <TextInput type="email" value={f.email} error={!!err.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
          </Field>
          <Field label={t('form.phone')}>
            <TextInput value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
          </Field>
        </div>
        <Field label={t('form.notes')}>
          <Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
        </Field>
        <FormActions onCancel={onClose} busy={busy} />
      </form>
    </Modal>
  );
}

// ── Property ───────────────────────────────────────────────────────────────
export type PropertyRow = { id: string; name: string; property_type: string; address: string | null; client_id?: string | null; area_m2?: number | null; access_notes?: string | null };
export function PropertyForm({ open, onClose, onSaved, initial }: BaseProps<PropertyRow>) {
  const { t } = useTranslation();
  const { id: orgId } = useOrg();
  const [clients, setClients] = useState<{ id: string; name: string }[]>([]);
  const [f, setF] = useState({ name: '', property_type: 'other', client_id: '', address: '', area_m2: '', access_notes: '' });
  const [err, setErr] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setErr({});
    setF({
      name: initial?.name ?? '', property_type: initial?.property_type ?? 'other',
      client_id: initial?.client_id ?? '', address: initial?.address ?? '',
      area_m2: initial?.area_m2 != null ? String(initial.area_m2) : '', access_notes: initial?.access_notes ?? '',
    });
    supabase.from('clients').select('id,name').is('deleted_at', null).order('name').then((r) => setClients(r.data ?? []));
  }, [open, initial]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const er: Record<string, string> = {};
    if (!f.name.trim()) er.name = t('form.errRequired');
    if (f.area_m2.trim() && !(Number(f.area_m2) > 0)) er.area_m2 = t('form.errPositive');
    setErr(er);
    if (Object.keys(er).length) return;
    setBusy(true);
    const payload = {
      name: f.name.trim(), property_type: f.property_type, client_id: f.client_id || null,
      address: f.address.trim() || null, area_m2: f.area_m2.trim() ? Number(f.area_m2) : null,
      access_notes: f.access_notes.trim() || null,
    };
    const res = initial?.id
      ? await supabase.from('properties').update(payload).eq('id', initial.id)
      : await supabase.from('properties').insert({ ...payload, organization_id: orgId } as any);
    setBusy(false);
    if (res.error) { setErr({ _: dbErrorMessage(t, res.error) }); return; }
    onSaved(); onClose();
  }

  return (
    <Modal open={open} onClose={onClose} title={initial?.id ? t('form.editProperty') : t('form.newProperty')}>
      <form onSubmit={submit} className="flex flex-col gap-4 p-5">
        {err._ && <div className="rounded-md bg-st-overdue-bg px-3 py-2 text-sm text-st-overdue-fg">{err._}</div>}
        <Field label={t('form.name')} required error={err.name}>
          <TextInput value={f.name} error={!!err.name} onChange={(e) => setF({ ...f, name: e.target.value })} autoFocus />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('form.propertyType')}>
            <Select value={f.property_type} onChange={(e) => setF({ ...f, property_type: e.target.value })}>
              {PROPERTY_TYPES.map((p) => <option key={p} value={p}>{t('propType.' + p)}</option>)}
            </Select>
          </Field>
          <Field label={t('form.client')}>
            <Select value={f.client_id} onChange={(e) => setF({ ...f, client_id: e.target.value })}>
              <option value="">{t('form.none')}</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </Field>
        </div>
        <Field label={t('form.address')}>
          <TextInput value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} />
        </Field>
        <Field label={t('form.area')} error={err.area_m2}>
          <TextInput type="number" min="0" step="0.01" value={f.area_m2} error={!!err.area_m2} onChange={(e) => setF({ ...f, area_m2: e.target.value })} />
        </Field>
        <Field label={t('form.accessNotes')}>
          <Textarea value={f.access_notes} onChange={(e) => setF({ ...f, access_notes: e.target.value })} />
        </Field>
        <FormActions onCancel={onClose} busy={busy} />
      </form>
    </Modal>
  );
}

// ── Asset (equipment) ────────────────────────────────────────────────────────
export type AssetRow = {
  id: string; name: string; property_id: string; zone_id?: string | null; category_id?: string | null;
  criticality: string; availability: string; brand?: string | null; model?: string | null;
  serial_number?: string | null; in_service_date?: string | null;
};
export function AssetForm({ open, onClose, onSaved, initial }: BaseProps<AssetRow>) {
  const { t } = useTranslation();
  const { id: orgId } = useOrg();
  const [props, setProps] = useState<{ id: string; name: string }[]>([]);
  const [zones, setZones] = useState<{ id: string; name: string }[]>([]);
  const [cats, setCats] = useState<{ id: string; name: string }[]>([]);
  const [f, setF] = useState({
    name: '', property_id: '', zone_id: '', category_id: '', criticality: 'medium',
    availability: 'available', brand: '', model: '', serial_number: '', in_service_date: '',
  });
  const [err, setErr] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setErr({});
    setF({
      name: initial?.name ?? '', property_id: initial?.property_id ?? '', zone_id: initial?.zone_id ?? '',
      category_id: initial?.category_id ?? '', criticality: initial?.criticality ?? 'medium',
      availability: initial?.availability ?? 'available', brand: initial?.brand ?? '',
      model: initial?.model ?? '', serial_number: initial?.serial_number ?? '', in_service_date: initial?.in_service_date ?? '',
    });
    supabase.from('properties').select('id,name').is('deleted_at', null).order('name').then((r) => setProps(r.data ?? []));
    supabase.from('asset_categories').select('id,name').is('deleted_at', null).order('name').then((r) => setCats(r.data ?? []));
  }, [open, initial]);

  // zones depend on the chosen property
  useEffect(() => {
    if (!open || !f.property_id) { setZones([]); return; }
    supabase.from('zones').select('id,name').eq('property_id', f.property_id).is('deleted_at', null).order('name').then((r) => setZones(r.data ?? []));
  }, [open, f.property_id]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const er: Record<string, string> = {};
    if (!f.name.trim()) er.name = t('form.errRequired');
    if (!f.property_id) er.property_id = t('form.errRequired');
    setErr(er);
    if (Object.keys(er).length) return;
    setBusy(true);
    const payload = {
      name: f.name.trim(), property_id: f.property_id, zone_id: f.zone_id || null,
      category_id: f.category_id || null, criticality: f.criticality, availability: f.availability,
      brand: f.brand.trim() || null, model: f.model.trim() || null,
      serial_number: f.serial_number.trim() || null, in_service_date: f.in_service_date || null,
    };
    const res = initial?.id
      ? await supabase.from('assets').update(payload).eq('id', initial.id)
      : await supabase.from('assets').insert({ ...payload, organization_id: orgId } as any);
    setBusy(false);
    if (res.error) { setErr({ _: dbErrorMessage(t, res.error) }); return; }
    onSaved(); onClose();
  }

  return (
    <Modal open={open} onClose={onClose} title={initial?.id ? t('form.editAsset') : t('form.newAsset')} wide>
      <form onSubmit={submit} className="flex flex-col gap-4 p-5">
        {err._ && <div className="rounded-md bg-st-overdue-bg px-3 py-2 text-sm text-st-overdue-fg">{err._}</div>}
        <Field label={t('form.name')} required error={err.name}>
          <TextInput value={f.name} error={!!err.name} onChange={(e) => setF({ ...f, name: e.target.value })} autoFocus />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('form.property')} required error={err.property_id}>
            <Select value={f.property_id} error={!!err.property_id} onChange={(e) => setF({ ...f, property_id: e.target.value, zone_id: '' })}>
              <option value="">{t('form.selectOne')}</option>
              {props.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
          </Field>
          <Field label={t('form.zone')}>
            <Select value={f.zone_id} onChange={(e) => setF({ ...f, zone_id: e.target.value })} disabled={!f.property_id}>
              <option value="">{t('form.none')}</option>
              {zones.map((z) => <option key={z.id} value={z.id}>{z.name}</option>)}
            </Select>
          </Field>
          <Field label={t('form.category')}>
            <Select value={f.category_id} onChange={(e) => setF({ ...f, category_id: e.target.value })}>
              <option value="">{t('form.none')}</option>
              {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </Field>
          <Field label={t('form.inServiceDate')} hint={t('form.inServiceHint')}>
            <TextInput type="date" value={f.in_service_date} onChange={(e) => setF({ ...f, in_service_date: e.target.value })} />
          </Field>
          <Field label={t('form.criticality')}>
            <Select value={f.criticality} onChange={(e) => setF({ ...f, criticality: e.target.value })}>
              {CRITICALITY.map((c) => <option key={c} value={c}>{t('criticality.' + c)}</option>)}
            </Select>
          </Field>
          <Field label={t('form.availability')}>
            <Select value={f.availability} onChange={(e) => setF({ ...f, availability: e.target.value })}>
              {AVAILABILITY.map((a) => <option key={a} value={a}>{t('availability.' + a)}</option>)}
            </Select>
          </Field>
          <Field label={t('form.brand')}>
            <TextInput value={f.brand} onChange={(e) => setF({ ...f, brand: e.target.value })} />
          </Field>
          <Field label={t('form.model')}>
            <TextInput value={f.model} onChange={(e) => setF({ ...f, model: e.target.value })} />
          </Field>
        </div>
        <Field label={t('form.serial')}>
          <TextInput value={f.serial_number} onChange={(e) => setF({ ...f, serial_number: e.target.value })} />
        </Field>
        <FormActions onCancel={onClose} busy={busy} />
      </form>
    </Modal>
  );
}

// ── Work order ───────────────────────────────────────────────────────────────
export type WorkOrderRow = {
  id: string; wo_type: string; priority: string; property_id: string; asset_id?: string | null; assigned_to?: string | null;
};
export function WorkOrderForm({ open, onClose, onSaved, initial }: BaseProps<WorkOrderRow>) {
  const { t } = useTranslation();
  const { id: orgId } = useOrg();
  const [props, setProps] = useState<{ id: string; name: string }[]>([]);
  const [assets, setAssets] = useState<{ id: string; name: string }[]>([]);
  const [members, setMembers] = useState<{ user_id: string; name: string }[]>([]);
  const [f, setF] = useState({ wo_type: 'corrective', priority: 'medium', property_id: '', asset_id: '', assigned_to: '' });
  const [err, setErr] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setErr({});
    setF({
      wo_type: initial?.wo_type ?? 'corrective', priority: initial?.priority ?? 'medium',
      property_id: initial?.property_id ?? '', asset_id: initial?.asset_id ?? '', assigned_to: initial?.assigned_to ?? '',
    });
    supabase.from('properties').select('id,name').is('deleted_at', null).order('name').then((r) => setProps(r.data ?? []));
    Promise.all([
      supabase.from('memberships').select('user_id').is('deleted_at', null),
      supabase.from('user_profiles').select('user_id,display_name').is('deleted_at', null),
    ]).then(([m, p]) => {
      const names: Record<string, string> = {};
      ((p.data as { user_id: string; display_name: string | null }[]) ?? []).forEach((x) => { if (x.display_name) names[x.user_id] = x.display_name; });
      // only list assignees with a real profile name — never an id-derived label
      setMembers(((m.data as { user_id: string }[]) ?? [])
        .map((x) => ({ user_id: x.user_id, name: names[x.user_id] }))
        .filter((x): x is { user_id: string; name: string } => !!x.name));
    });
  }, [open, initial]);

  useEffect(() => {
    if (!open || !f.property_id) { setAssets([]); return; }
    supabase.from('assets').select('id,name').eq('property_id', f.property_id).is('deleted_at', null).order('name').then((r) => setAssets(r.data ?? []));
  }, [open, f.property_id]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const er: Record<string, string> = {};
    if (!f.property_id) er.property_id = t('form.errRequired');
    setErr(er);
    if (Object.keys(er).length) return;
    setBusy(true);
    const payload = {
      wo_type: f.wo_type, priority: f.priority, property_id: f.property_id,
      asset_id: f.asset_id || null, assigned_to: f.assigned_to || null,
    };
    const res = initial?.id
      ? await supabase.from('work_orders').update(payload).eq('id', initial.id)
      : await supabase.from('work_orders').insert({ ...payload, organization_id: orgId } as any);
    setBusy(false);
    if (res.error) { setErr({ _: dbErrorMessage(t, res.error) }); return; }
    onSaved(); onClose();
  }

  return (
    <Modal open={open} onClose={onClose} title={initial?.id ? t('form.editWorkOrder') : t('form.newWorkOrder')}>
      <form onSubmit={submit} className="flex flex-col gap-4 p-5">
        {err._ && <div className="rounded-md bg-st-overdue-bg px-3 py-2 text-sm text-st-overdue-fg">{err._}</div>}
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('form.woType')}>
            <Select value={f.wo_type} onChange={(e) => setF({ ...f, wo_type: e.target.value })}>
              {WO_TYPES.map((w) => <option key={w} value={w}>{t('woType.' + w)}</option>)}
            </Select>
          </Field>
          <Field label={t('form.priority')}>
            <Select value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })}>
              {PRIORITIES.map((p) => <option key={p} value={p}>{t('priority.' + p)}</option>)}
            </Select>
          </Field>
        </div>
        <Field label={t('form.property')} required error={err.property_id}>
          <Select value={f.property_id} error={!!err.property_id} onChange={(e) => setF({ ...f, property_id: e.target.value, asset_id: '' })} autoFocus>
            <option value="">{t('form.selectOne')}</option>
            {props.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
        </Field>
        <Field label={t('form.property') + ' · ' + t('nav.assets')}>
          <Select value={f.asset_id} onChange={(e) => setF({ ...f, asset_id: e.target.value })} disabled={!f.property_id}>
            <option value="">{t('form.none')}</option>
            {assets.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </Select>
        </Field>
        <Field label={t('form.assignTo')}>
          <Select value={f.assigned_to} onChange={(e) => setF({ ...f, assigned_to: e.target.value })}>
            <option value="">{t('form.unassigned')}</option>
            {members.map((m) => <option key={m.user_id} value={m.user_id}>{m.name}</option>)}
          </Select>
        </Field>
        <FormActions onCancel={onClose} busy={busy} />
      </form>
    </Modal>
  );
}

// ── Zone ───────────────────────────────────────────────────────────────────
export type ZoneRow = { id: string; name: string; zone_type?: string | null; area_m2?: number | null };
export function ZoneForm({ open, onClose, onSaved, initial, propertyId }: BaseProps<ZoneRow> & { propertyId: string }) {
  const { t } = useTranslation();
  const { id: orgId } = useOrg();
  const [f, setF] = useState({ name: '', zone_type: '', area_m2: '' });
  const [err, setErr] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setErr({});
    setF({ name: initial?.name ?? '', zone_type: initial?.zone_type ?? '', area_m2: initial?.area_m2 != null ? String(initial.area_m2) : '' });
  }, [open, initial]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const er: Record<string, string> = {};
    if (!f.name.trim()) er.name = t('form.errRequired');
    if (f.area_m2.trim() && !(Number(f.area_m2) > 0)) er.area_m2 = t('form.errPositive');
    setErr(er);
    if (Object.keys(er).length) return;
    setBusy(true);
    const payload = { name: f.name.trim(), zone_type: f.zone_type.trim() || null, area_m2: f.area_m2.trim() ? Number(f.area_m2) : null };
    const res = initial?.id
      ? await supabase.from('zones').update(payload).eq('id', initial.id)
      : await supabase.from('zones').insert({ ...payload, organization_id: orgId, property_id: propertyId } as any);
    setBusy(false);
    if (res.error) { setErr({ _: dbErrorMessage(t, res.error) }); return; }
    onSaved(); onClose();
  }

  return (
    <Modal open={open} onClose={onClose} title={initial?.id ? t('form.editZone') : t('form.newZone')}>
      <form onSubmit={submit} className="flex flex-col gap-4 p-5">
        {err._ && <div className="rounded-md bg-st-overdue-bg px-3 py-2 text-sm text-st-overdue-fg">{err._}</div>}
        <Field label={t('form.name')} required error={err.name}>
          <TextInput value={f.name} error={!!err.name} onChange={(e) => setF({ ...f, name: e.target.value })} autoFocus />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('form.type')}>
            <TextInput value={f.zone_type} onChange={(e) => setF({ ...f, zone_type: e.target.value })} />
          </Field>
          <Field label={t('form.area')} error={err.area_m2}>
            <TextInput type="number" min="0" step="0.01" value={f.area_m2} error={!!err.area_m2} onChange={(e) => setF({ ...f, area_m2: e.target.value })} />
          </Field>
        </div>
        <FormActions onCancel={onClose} busy={busy} />
      </form>
    </Modal>
  );
}

// ── Invitation (role + property scope) ─────────────────────────────────────────
// app roles → which app the invite link opens (base URL from env; web = current origin)
const APP_ROLES = ['admin', 'manager', 'finance', 'technician', 'subcontractor', 'owner', 'villa_manager', 'management_partner', 'referrer'] as const;
const SCOPED_ROLES = ['owner', 'villa_manager', 'management_partner'];
function appBase(appKey: string): string {
  const env = (import.meta as any).env ?? {};
  if (appKey === 'field') return env.VITE_FIELD_URL ?? '';
  if (appKey === 'portal') return env.VITE_PORTAL_URL ?? '';
  return typeof window !== 'undefined' ? window.location.origin : '';
}
function inviteLink(appKey: string, token: string): string {
  return `${appBase(appKey)}/set-password?token=${token}`;
}

export function InviteForm({ open, onClose, onSaved }: Omit<BaseProps<never>, 'initial'>) {
  const { t } = useTranslation();
  const { id: orgId } = useOrg();
  const [props, setProps] = useState<{ id: string; name: string }[]>([]);
  const [f, setF] = useState({ email: '', app_role: 'technician', scope: [] as string[] });
  const [err, setErr] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<{ link: string; appKey: string } | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    setErr({}); setCreated(null); setCopied(false);
    setF({ email: '', app_role: 'technician', scope: [] });
    supabase.from('properties').select('id,name').is('deleted_at', null).order('name').then((r) => setProps(r.data ?? []));
  }, [open]);

  function toggleScope(id: string) {
    setF((p) => ({ ...p, scope: p.scope.includes(id) ? p.scope.filter((x) => x !== id) : [...p.scope, id] }));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const er: Record<string, string> = {};
    if (!f.email.trim()) er.email = t('form.errRequired');
    else if (!EMAIL_RE.test(f.email.trim())) er.email = t('form.errEmail');
    setErr(er);
    if (Object.keys(er).length) return;
    setBusy(true);
    const scope = SCOPED_ROLES.includes(f.app_role) ? f.scope : [];
    const r = await supabase.rpc('create_invitation', {
      p_org: orgId, p_email: f.email.trim(), p_app_role: f.app_role, p_property_ids: scope,
    });
    if (r.error || !r.data) { setBusy(false); setErr({ _: dbErrorMessage(t, r.error) }); return; }
    const appKey = r.data.app_key;
    // Provision the auth user + account and get a real activation link (edge fn,
    // service role auto-injected). Falls back to the plain token link if the fn
    // is not deployed, so the back-office still works without it.
    let link = inviteLink(appKey, r.data.token);
    const prov = await supabase.functions.invoke('invite-user', {
      body: { invitation_id: r.data.invitation_id, redirect_base: appBase(appKey) },
    });
    if (!prov.error && (prov.data as any)?.action_link) link = (prov.data as any).action_link;
    setBusy(false);
    setCreated({ link, appKey });
    onSaved();
  }

  return (
    <Modal open={open} onClose={onClose} title={t('form.inviteUser')}>
      {created ? (
        <div className="flex flex-col gap-4 p-5">
          <div className="rounded-md bg-st-done-bg px-3 py-2 text-sm text-st-done-fg">{t('invite.created', { app: t('invite.app.' + created.appKey) })}</div>
          <Field label={t('invite.link')}>
            <div className="flex gap-2">
              <TextInput readOnly value={created.link} onFocus={(e) => e.currentTarget.select()} />
              <button type="button" className="shrink-0 rounded-md border border-border px-3 text-sm" onClick={() => { void navigator.clipboard?.writeText(created.link); setCopied(true); }}>{copied ? t('invite.copied') : t('invite.copy')}</button>
            </div>
          </Field>
          <a className="rounded-md bg-[#25D366] px-3 py-2 text-center text-sm font-medium text-white" href={`https://wa.me/?text=${encodeURIComponent(created.link)}`} target="_blank" rel="noopener noreferrer">{t('invite.whatsapp')}</a>
          <div className="flex justify-end gap-2">
            <button type="button" className="rounded-md border border-border px-3 py-2 text-sm" onClick={() => { setCreated(null); setF({ email: '', app_role: 'technician', scope: [] }); }}>{t('invite.another')}</button>
            <button type="button" className="rounded-md bg-fg px-3 py-2 text-sm text-white" onClick={onClose}>{t('common.done')}</button>
          </div>
        </div>
      ) : (
      <form onSubmit={submit} className="flex flex-col gap-4 p-5">
        {err._ && <div className="rounded-md bg-st-overdue-bg px-3 py-2 text-sm text-st-overdue-fg">{err._}</div>}
        <Field label={t('form.email')} required error={err.email}>
          <TextInput type="email" value={f.email} error={!!err.email} onChange={(e) => setF({ ...f, email: e.target.value })} autoFocus />
        </Field>
        <Field label={t('form.role')} hint={t('invite.roleHint')}>
          <Select value={f.app_role} onChange={(e) => setF({ ...f, app_role: e.target.value })}>
            {APP_ROLES.map((r) => <option key={r} value={r}>{t('appRole.' + r)}</option>)}
          </Select>
        </Field>
        {SCOPED_ROLES.includes(f.app_role) && (
          <Field label={t('form.scope')} hint={t('form.scopeHint')}>
            <div className="flex max-h-44 flex-col gap-1 overflow-y-auto rounded-md border border-border p-2">
              {props.map((p) => (
                <label key={p.id} className="flex items-center gap-2 rounded px-1.5 py-1 text-sm hover:bg-bg">
                  <input type="checkbox" checked={f.scope.includes(p.id)} onChange={() => toggleScope(p.id)} />
                  {p.name}
                </label>
              ))}
              {props.length === 0 && <span className="px-1.5 py-1 text-sm text-muted">{t('common.noData')}</span>}
            </div>
          </Field>
        )}
        <FormActions onCancel={onClose} busy={busy} submitLabel={t('form.inviteUser')} />
      </form>
      )}
    </Modal>
  );
}

// ── Supplier ─────────────────────────────────────────────────────────────────
export type SupplierRow = { id: string; name: string; email: string | null; phone: string | null; notes?: string | null };
export function SupplierForm({ open, onClose, onSaved, initial }: BaseProps<SupplierRow>) {
  const { t } = useTranslation();
  const { id: orgId } = useOrg();
  const [f, setF] = useState({ name: '', email: '', phone: '', notes: '' });
  const [err, setErr] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return; setErr({});
    setF({ name: initial?.name ?? '', email: initial?.email ?? '', phone: initial?.phone ?? '', notes: initial?.notes ?? '' });
  }, [open, initial]);
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const er: Record<string, string> = {};
    if (!f.name.trim()) er.name = t('form.errRequired');
    if (f.email.trim() && !EMAIL_RE.test(f.email.trim())) er.email = t('form.errEmail');
    setErr(er); if (Object.keys(er).length) return;
    setBusy(true);
    const payload = { name: f.name.trim(), email: f.email.trim() || null, phone: f.phone.trim() || null, notes: f.notes.trim() || null };
    const res = initial?.id
      ? await supabase.from('suppliers').update(payload).eq('id', initial.id)
      : await supabase.from('suppliers').insert({ ...payload, organization_id: orgId } as any);
    setBusy(false);
    if (res.error) { setErr({ _: dbErrorMessage(t, res.error) }); return; }
    onSaved(); onClose();
  }
  return (
    <Modal open={open} onClose={onClose} title={initial?.id ? t('supplier.edit') : t('supplier.new')}>
      <form onSubmit={submit} className="flex flex-col gap-4 p-5">
        {err._ && <div className="rounded-md bg-st-overdue-bg px-3 py-2 text-sm text-st-overdue-fg">{err._}</div>}
        <Field label={t('form.name')} required error={err.name}><TextInput value={f.name} error={!!err.name} onChange={(e) => setF({ ...f, name: e.target.value })} autoFocus /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('form.email')} error={err.email}><TextInput type="email" value={f.email} error={!!err.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
          <Field label={t('form.phone')}><TextInput value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></Field>
        </div>
        <Field label={t('form.notes')}><Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field>
        <FormActions onCancel={onClose} busy={busy} />
      </form>
    </Modal>
  );
}

// ── Part (catalog + stock) ───────────────────────────────────────────────────
export type PartRow = { id: string; name: string; sku: string | null; unit: string | null; unit_cost: number | null; on_hand: number; reorder_level: number | null; supplier_id: string | null; category_id?: string | null; location_id?: string | null };
export function PartForm({ open, onClose, onSaved, initial }: BaseProps<PartRow>) {
  const { t } = useTranslation();
  const { id: orgId, currency } = useOrg();
  const [suppliers, setSuppliers] = useState<{ id: string; name: string }[]>([]);
  const [cats, setCats] = useState<{ id: string; name: string }[]>([]);
  const [locs, setLocs] = useState<{ id: string; name: string }[]>([]);
  const [f, setF] = useState({ name: '', sku: '', unit: '', unit_cost: '', on_hand: '0', reorder_level: '', supplier_id: '', category_id: '', location_id: '' });
  const [err, setErr] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return; setErr({});
    setF({
      name: initial?.name ?? '', sku: initial?.sku ?? '', unit: initial?.unit ?? '',
      unit_cost: initial?.unit_cost != null ? String(initial.unit_cost) : '',
      on_hand: initial?.on_hand != null ? String(initial.on_hand) : '0',
      reorder_level: initial?.reorder_level != null ? String(initial.reorder_level) : '',
      supplier_id: initial?.supplier_id ?? '', category_id: initial?.category_id ?? '', location_id: initial?.location_id ?? '',
    });
    supabase.from('suppliers').select('id,name').is('deleted_at', null).order('name').then((r) => setSuppliers(r.data ?? []));
    supabase.from('inventory_categories').select('id,name').eq('kind', 'part').is('deleted_at', null).order('name').then((r) => setCats((r.data as any) ?? []));
    supabase.from('inventory_locations').select('id,name').is('deleted_at', null).order('name').then((r) => setLocs((r.data as any) ?? []));
  }, [open, initial]);
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const er: Record<string, string> = {};
    if (!f.name.trim()) er.name = t('form.errRequired');
    setErr(er); if (Object.keys(er).length) return;
    setBusy(true);
    const num = (s: string) => (s.trim() === '' ? null : Number(s));
    const payload = {
      name: f.name.trim(), sku: f.sku.trim() || null, unit: f.unit.trim() || null,
      unit_cost: num(f.unit_cost), on_hand: Number(f.on_hand) || 0, reorder_level: num(f.reorder_level),
      supplier_id: f.supplier_id || null, category_id: f.category_id || null, location_id: f.location_id || null,
    };
    const res = initial?.id
      ? await supabase.from('parts').update(payload).eq('id', initial.id)
      : await supabase.from('parts').insert({ ...payload, organization_id: orgId, currency } as any);
    setBusy(false);
    if (res.error) { setErr({ _: dbErrorMessage(t, res.error) }); return; }
    onSaved(); onClose();
  }
  return (
    <Modal open={open} onClose={onClose} title={initial?.id ? t('part.edit') : t('part.new')} wide>
      <form onSubmit={submit} className="flex flex-col gap-4 p-5">
        {err._ && <div className="rounded-md bg-st-overdue-bg px-3 py-2 text-sm text-st-overdue-fg">{err._}</div>}
        <Field label={t('form.name')} required error={err.name}><TextInput value={f.name} error={!!err.name} onChange={(e) => setF({ ...f, name: e.target.value })} autoFocus /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('part.sku')}><TextInput value={f.sku} onChange={(e) => setF({ ...f, sku: e.target.value })} /></Field>
          <Field label={t('part.unit')}><TextInput value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} /></Field>
          <Field label={t('inv.category')}>
            <Select value={f.category_id} onChange={(e) => setF({ ...f, category_id: e.target.value })}>
              <option value="">{t('form.none')}</option>
              {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </Field>
          <Field label={t('inv.location')}>
            <Select value={f.location_id} onChange={(e) => setF({ ...f, location_id: e.target.value })}>
              <option value="">{t('form.none')}</option>
              {locs.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </Select>
          </Field>
          <Field label={t('part.unitCost') + ` (${currency})`}><TextInput type="number" min="0" step="0.01" value={f.unit_cost} onChange={(e) => setF({ ...f, unit_cost: e.target.value })} /></Field>
          <Field label={t('part.supplier')}>
            <Select value={f.supplier_id} onChange={(e) => setF({ ...f, supplier_id: e.target.value })}>
              <option value="">{t('form.none')}</option>
              {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </Select>
          </Field>
          <Field label={t('part.onHand')} hint={t('part.onHandHint')}><TextInput type="number" step="0.001" value={f.on_hand} disabled={!!initial?.id} onChange={(e) => setF({ ...f, on_hand: e.target.value })} /></Field>
          <Field label={t('part.reorder')}><TextInput type="number" step="0.001" value={f.reorder_level} onChange={(e) => setF({ ...f, reorder_level: e.target.value })} /></Field>
        </div>
        {initial?.id && <PhotoStrip itemType="part" itemId={initial.id} />}
        <FormActions onCancel={onClose} busy={busy} />
      </form>
    </Modal>
  );
}
